import _ from 'lodash'
import { createSelector } from 'reselect'
import { creditCardOption } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { userEmailOption } from 'src/components/UserDropdown/UserDropdown'
import { OrganizationRoles } from 'src/enums'
import { RootState } from 'src/redux/createRootReducer'
import { CreditCardsFullPaginatedDataState } from 'src/redux/creditCardsFullPaginatedData.reducer'
import { RecentUsersFullPaginatedDataState } from 'src/redux/recentUsersFullPaginatedData.reducer'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectReceiptAttachmentsListModels } from 'src/redux/selectors/data/receiptAttachments.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectVirtualCardPushTaskListModelsFromState } from 'src/redux/selectors/data/virtualCardPushTasks.selectors'
import { UsersFullPaginatedDataState } from 'src/redux/usersFullPaginatedData.reducer'
import { ErrorDetailMap } from 'src/utils/error.utils'

const selectReceiptAttachmentIdsFromState = (state: RootState): string[] =>
  state.app.createVirtualCardForm.receiptAttachmentIds

export const selectReceiptAttachmentModelsList = createSelector(
  [selectReceiptAttachmentIdsFromState, selectReceiptAttachmentsListModels],
  (ids, receiptAttachments) => ids.map((id) => receiptAttachments[id]),
)

export const selectUploadingAttachmentsFromState = (
  state: RootState,
): Record<string, string> =>
  state.app.createVirtualCardForm.uploadingAttachmentThumbnails

export const selectPostingVirtualCardFromState = (state: RootState): boolean =>
  state.app.createVirtualCardForm.postingVirtualCard

export const selectTopLevelErrorFromState = (
  state: RootState,
): string | undefined => state.app.createVirtualCardForm.topLevelError

export const selectErrorDetailMapFromState = (
  state: RootState,
): ErrorDetailMap<string> | undefined =>
  state.app.createVirtualCardForm.errorDetailMap

export const selectMultipleTopLevelErrorFromState = (
  state: RootState,
): string | undefined => state.app.createVirtualCardForm.multipleTopLevelError

export const selectMultipleErrorDetailMapFromState = (
  state: RootState,
): ErrorDetailMap<string> | undefined =>
  state.app.createVirtualCardForm.multipleErrorDetailMap

export const selectBulkTemplateFromState = (
  state: RootState,
): string | undefined => state.app.createVirtualCardForm.bulkTemplate

export const selectLoadingBulkTemplateFromState = (
  state: RootState,
): boolean | undefined => state.app.createVirtualCardForm.loadingBulkTemplate

export const selectTemplateTypeFromState = (
  state: RootState,
): string | undefined => state.app.createVirtualCardForm.templateType

export const selectPostingCreditCardBulkVirtualCards = (
  state: RootState,
): boolean => state.app.createVirtualCardForm.postingCreditCardBulkVirtualCards

export const selectTaskIdsFromState = (
  state: RootState,
): string[] | undefined => state.app.createVirtualCardForm.taskIds
export const selectVirtualCardPushTasks = createSelector(
  [selectVirtualCardPushTaskListModelsFromState, selectTaskIdsFromState],
  (pushTasks, taskIds) => taskIds?.map((taskId) => pushTasks[taskId]),
)

export const selectUsersFullPaginatedData = (
  state: RootState,
): UsersFullPaginatedDataState =>
  state.app.createVirtualCardForm.usersFullPaginatedData

const selectUniqueUserIds = createSelector(
  [selectUsersFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueUserEmailOptions = createSelector(
  [selectUniqueUserIds, selectUserListModelsFromState],
  (ids, users) => ids.map((id) => userEmailOption(users[id])),
)

export const selectRecentUsersFullPaginatedData = (
  state: RootState,
): RecentUsersFullPaginatedDataState =>
  state.app.createVirtualCardForm.recentUsersFullPaginatedData

const selectUniqueRecentUserIds = createSelector(
  [selectRecentUsersFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueRecentUserEmailOptions = createSelector(
  [selectUniqueRecentUserIds, selectUserListModelsFromState],
  (ids, users) => ids.map((id) => userEmailOption(users[id])),
)

export const selectFilteredRecentUserEmailOptions = createSelector(
  [selectUniqueRecentUserEmailOptions, selectUserListModelFromState],
  (options, user) => {
    const ownUser = options.find(
      (option) => _.get(option.details, 'user.id', undefined) === user?.id,
    )
    const filteredOptions = options.filter(
      (option) =>
        _.get(option.details, 'user.id', undefined) !== user?.id &&
        (user?.hasOrgAdminPermissions ||
          user?.organizationRole === OrganizationRoles.cardManager ||
          user?.organizationId === option.details?.user?.organizationId),
    )
    if (ownUser) {
      filteredOptions.unshift(ownUser)
    }
    return filteredOptions.slice(0, 5)
  },
)

export const selectFilteredUserEmailOptions = createSelector(
  [selectUniqueUserEmailOptions, selectUserListModelFromState],
  (options, user) =>
    options.filter(
      (option) =>
        user?.hasOrgAdminPermissions ||
        user?.organizationRole === OrganizationRoles.cardManager ||
        user?.organizationId === option.details?.user?.organizationId,
    ),
)

export const selectCreditCardsFullPaginatedData = (
  state: RootState,
): CreditCardsFullPaginatedDataState =>
  state.app.createVirtualCardForm.creditCardsFullPaginatedData
const selectUniqueCreditCardIds = createSelector(
  [selectCreditCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectCreditCardOptions = createSelector(
  [selectUniqueCreditCardIds, selectCreditCardListModelsFromState],
  (ids, creditCards) => ids.map((id) => creditCardOption(creditCards[id])),
)
export const selectFilteredCreditCardOptions = createSelector(
  [selectCreditCardOptions],
  (options) => options.filter(() => true),
)

export const selectRecurringEnabledCreditCardsFullPaginatedData = (
  state: RootState,
): CreditCardsFullPaginatedDataState =>
  state.app.createVirtualCardForm.recurringEnabledCreditCardsFullPaginatedData
const selectUniqueRecurringEnabledCreditCardIds = createSelector(
  [selectRecurringEnabledCreditCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueRecurringEnabledCreditCardOptions = createSelector(
  [
    selectUniqueRecurringEnabledCreditCardIds,
    selectCreditCardListModelsFromState,
  ],
  (ids, creditCards) => ids.map((id) => creditCardOption(creditCards[id])),
)
export const selectFilteredRecurringEnabledCreditCardOptions = createSelector(
  [selectUniqueRecurringEnabledCreditCardOptions],
  (options) =>
    options.filter((option) => option.details?.creditCard.features.recurrence),
)

export const selectGuestAccessEnabledCreditCardsFullPaginatedData = (
  state: RootState,
): CreditCardsFullPaginatedDataState =>
  state.app.createVirtualCardForm.guestAccessEnabledCreditCardsFullPaginatedData
const selectUniqueGuestAccessEnabledCreditCardIds = createSelector(
  [selectGuestAccessEnabledCreditCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueGuestAccessEnabledCreditCardOptions = createSelector(
  [
    selectUniqueGuestAccessEnabledCreditCardIds,
    selectCreditCardListModelsFromState,
  ],
  (ids, creditCards) => ids.map((id) => creditCardOption(creditCards[id])),
)
export const selectFilteredGuestAccessEnabledCreditCardOptions = createSelector(
  [selectUniqueGuestAccessEnabledCreditCardOptions],
  (options) =>
    options.filter((option) => option.details?.creditCard.features.direct),
)

export const selectBalanceLockEnabledCreditCardsFullPaginatedData = (
  state: RootState,
): CreditCardsFullPaginatedDataState =>
  state.app.createVirtualCardForm
    .balanceLockedEnabledCreditCardsFullPaginatedData
const selectUniqueBalanceLockEnabledCreditCardIds = createSelector(
  [selectBalanceLockEnabledCreditCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueBalanceLockEnabledCreditCardOptions = createSelector(
  [
    selectUniqueBalanceLockEnabledCreditCardIds,
    selectCreditCardListModelsFromState,
  ],
  (ids, creditCards) => ids.map((id) => creditCardOption(creditCards[id])),
)
export const selectFilteredBalanceLockEnabledCreditCardOptions = createSelector(
  [selectUniqueBalanceLockEnabledCreditCardOptions],
  (options) => options.filter(() => true),
)

export const selectInvalidEmailsFromState = (
  state: RootState,
): string[] | undefined => state.app.createVirtualCardForm.invalidEmails
